import { ReactComponent as CheckedIcon } from '@assets/components/panel/sensors/checked-icon.svg';
import { Box, Button, Icon, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import SensorIcon from '@components/map/popup/infos/sensors/SensorIcon';
import { useDeleteSensorConfigurationMutation } from '@services/c2/c2.api';
import { getSensorIconKey } from '@utils/map/sensor.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { useSensorInstallationContext } from './SensorInstallationContext';

export function DeleteSensorModal({ open, onCancel }: Readonly<{ open: boolean; onCancel: () => void }>) {
  const { sensorToDelete, setSensorToDelete } = useSensorInstallationContext();
  const [deleteConfiguration, { isLoading }] = useDeleteSensorConfigurationMutation();

  const handleConfirm = async () => {
    if (!sensorToDelete) return;

    deleteConfiguration({
      site: sensorToDelete.site,
      appCode: sensorToDelete.appCode,
      id: sensorToDelete.id,
    })
      .unwrap()
      .finally(() => {
        onCancel();
        setSensorToDelete(null);
      });
  };

  return (
    <CockpitDialog open={open} onConfirm={handleConfirm} isLoading={isLoading} onCancel={onCancel}>
      <Text textAlign="center">
        <FormattedMessage id="components.sensorInstallation.delete" values={{ name: sensorToDelete?.name }} />
      </Text>
    </CockpitDialog>
  );
}

export function CreatedSensorModal({
  open,
  onCancel,
  onConfirm,
}: Readonly<{ open: boolean; onCancel: () => void; onConfirm: (value: string) => void }>) {
  const { formatMessage } = useIntl();
  const { createdSensor } = useSensorInstallationContext();

  if (!createdSensor) {
    return null;
  }

  return (
    <CockpitDialog open={open} onCancel={onCancel} showCancelButton={false} showContentInfo={false}>
      <VStack width="100%" gap={4}>
        <Box position="relative">
          <SensorIcon size="lg" sensorIconName={getSensorIconKey(createdSensor.type, false, false, undefined)} />
          <Icon as={CheckedIcon} position="absolute" boxSize="28px" top={0} right="-14px" />
        </Box>
        <Text textAlign="center">
          <FormattedMessage
            id="components.sensorInstallation.create"
            values={{ type: formatMessage({ id: `sensors.type.${createdSensor.type}` }) }}
          />
        </Text>
        <Text textAlign="center" whiteSpace="pre-line">
          <FormattedMessage id="components.sensorInstallation.createRedirection" />
        </Text>
        <Button width="302px" size="md" onClick={() => onConfirm(createdSensor.uniqueKey)}>
          <Text textStyle="sm" fontFamily="space" fontFeatureSettings="'liga' off">
            <FormattedMessage id="components.sensorInstallation.redirect" />
          </Text>
        </Button>
      </VStack>
    </CockpitDialog>
  );
}
